import type { FC } from 'react'
import { FastField, type FastFieldProps } from 'formik'
import { TextField, InputAdornment, Box, Autocomplete, inputBaseClasses, CircularProgress } from '@mui/material'
import { Check } from '@dhl/icons'
import { type FieldName, type Autocomplete as AutocompleteFieldFormat } from '@dhl/receiver-address-format-api'
import { identity } from '@dhl/utils'

import { AddressLabel } from './AddressLabel.js'

type Props = {
  name: string
  fieldName: FieldName
  fieldFormat: AutocompleteFieldFormat
  options: string[]
  isLoading: boolean
  disabled?: boolean
}

export const AddressAutocompleteFieldInput: FC<Props> = ({
  name,
  fieldName,
  fieldFormat,
  options,
  isLoading,
  disabled,
}) => {
  const isReadonly = (fieldFormat.isStrict && options.length === 1) || disabled

  return (
    <FastField name={name}>
      {({ field, meta }: FastFieldProps) => (
        <>
          {isReadonly && (
            <TextField
              {...field}
              name={field.name}
              value={field.value || ''}
              variant="outlined"
              required={fieldFormat.isRequired}
              label={<AddressLabel fieldName={fieldName} />}
              fullWidth
              disabled
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box display="flex" mr={1}>
                        <Check htmlColor="green" />
                      </Box>
                    </InputAdornment>
                  ),
                },
              }}
            />
          )}
          {!isReadonly && (
            <Autocomplete
              id={fieldName}
              {...field}
              onChange={(_, value: string | null) => field.onChange(field.name)(value ?? '')}
              freeSolo={!fieldFormat.isStrict}
              options={options}
              getOptionLabel={identity}
              fullWidth
              disabled={disabled}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={!!meta.error && meta.touched}
                  helperText={meta.touched && meta.error}
                  label={<AddressLabel fieldName={fieldName} />}
                  name={name}
                  onChange={(e) => {
                    if (!fieldFormat.isStrict) {
                      field.onChange(field.name)(e.target.value ?? '')
                    }
                  }}
                  required={fieldFormat.isRequired}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      name,
                      endAdornment: isLoading && (
                        <InputAdornment position="end">
                          <Box display="flex" mr={1}>
                            <CircularProgress size={24} />
                          </Box>
                        </InputAdornment>
                      ),
                    },
                    htmlInput: {
                      ...params.inputProps,
                      autoComplete: 'new-password', // disable autocomplete and autofill
                    },
                  }}
                  sx={{
                    [`& .${inputBaseClasses.input}`]: {
                      pl: 2,
                    },
                  }}
                />
              )}
            />
          )}
        </>
      )}
    </FastField>
  )
}
